import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  APPEAL_STATUS_OPTIONS,
  fetchAppealTypes,
  findAllAppealContributions,
  formatCurrency,
  formatSapDate,
  getLoggedInEmpId,
  getReferenceNumber,
  getStatusBadgeClass,
  getStatusLabel,
  pickField,
  syncAppealContribution,
} from "./appealContribution.service";

const AppealContributionList: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [appealTypeMap, setAppealTypeMap] = useState<Record<string, string>>({});

  const loadList = useCallback(
    async (
      nextPage = 1,
      overrides?: { fromDate?: string; toDate?: string; status?: string }
    ) => {
      const start = overrides?.fromDate ?? fromDate;
      const end = overrides?.toDate ?? toDate;
      const statusFilter = overrides?.status ?? status;
      if (start && end && start > end) {
        toast.error(t("start_date_error"));
        return;
      }
      setLoading(true);
      try {
        const result = await findAllAppealContributions({
          page: nextPage,
          limit: 10,
          status: statusFilter,
          fromDate: start || "2020-01-01",
          toDate: end || "2026-12-31",
          search: "",
        });
        setItems(result.items);
        setPage(result.page);
        setLastPage(result.lastPage);
      } catch (err: any) {
        setItems([]);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, status, t]
  );

  useEffect(() => {
    loadList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const options = await fetchAppealTypes();
        if (cancelled) return;
        const map: Record<string, string> = {};
        options.forEach((opt) => {
          if (opt.value !== "") map[opt.value] = opt.label;
        });
        setAppealTypeMap(map);
      } catch {
        /* list still works with raw type codes */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSync = async () => {
    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error(t("employee_id_not_found"));
      return;
    }
    setSyncing(true);
    try {
      const res: any = await syncAppealContribution(empId);
      toast.success(res?.message || t("synced_successfully"));
      await loadList(1);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
    } finally {
      setSyncing(false);
    }
  };

  const resolveAppealType = (value: any) => {
    const key = String(value ?? "");
    if (!key || key === "-") return "-";
    return appealTypeMap[key] || key;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("contribution_on_appeal")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>{t("appeal_for_contribution")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("contribution_on_appeal")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleSync}
              disabled={syncing || loading}
            >
              <i className={`ti ti-refresh me-1 ${syncing ? "spin" : ""}`}></i>
              {syncing ? t("syncing") : t("sync")}
            </button>
            <Link to={routes.appealContribution.create} className="btn btn-danger">
              <i className="ti ti-plus me-1"></i>
              {t("create_new_request")}
            </Link>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("start_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFromDate(value);
                    loadList(1, { fromDate: value });
                  }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("end_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => {
                    const value = e.target.value;
                    setToDate(value);
                    loadList(1, { toDate: value });
                  }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("filter_by_status")}</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStatus(value);
                    loadList(1, { status: value });
                  }}
                >
                  {APPEAL_STATUS_OPTIONS.map((opt) => {
                    const label =
                      t(opt.labelKey) === opt.labelKey ? opt.label : t(opt.labelKey);
                    return (
                      <option key={opt.key || "all"} value={opt.key}>
                        {opt.key ? `${label} (${opt.key})` : label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger flex-fill"
                  onClick={() => loadList(1)}
                  disabled={loading}
                >
                  {t("refresh_list")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setStatus("");
                    loadList(1, { fromDate: "", toDate: "", status: "" });
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : items.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">
              {t("no_appeal_live")}
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {items.map((item, idx) => {
              const refNo = getReferenceNumber(item);
              const rawStatus = pickField(
                item,
                "RequestStatus",
                "requestStatus",
                "Status",
                "status"
              );
              return (
                <div className="col-xl-6" key={refNo + idx}>
                  <div className="card border shadow-sm h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold">
                        {t("reference_no")}: {refNo}
                      </span>
                      <span className={getStatusBadgeClass(rawStatus)}>
                        {getStatusLabel(rawStatus, t)}
                      </span>
                    </div>
                    <div className="card-body">
                      <InfoRow
                        label={t("emp_id")}
                        value={pickField(item, "EmpID", "empId", "PersonalNo", "personalNo")}
                      />
                      <InfoRow
                        label={t("employee_name")}
                        value={pickField(item, "EmployeeName", "employeeName")}
                      />
                      <InfoRow
                        label={t("department")}
                        value={pickField(item, "Department", "department")}
                      />
                      <InfoRow
                        label={t("creation_date")}
                        value={formatSapDate(
                          pickField(
                            item,
                            "RequestCreationDate",
                            "requestCreationDate",
                            "CreationDate",
                            "creationDate",
                            "created_at"
                          )
                        )}
                      />
                      <InfoRow
                        label={t("type_of_appeal_required")}
                        value={resolveAppealType(
                          pickField(
                            item,
                            "TypeOfAppeal",
                            "typeOfAppeal",
                            "AppealType",
                            "appealType"
                          )
                        )}
                      />
                      <InfoRow
                        label={t("willingness_required")}
                        value={pickField(item, "Wilingness", "Willingness", "willingness")}
                      />
                      <InfoRow
                        label={t("deceased_employees")}
                        value={pickField(
                          item,
                          "DeceasedEmployeeName",
                          "deceasedEmployeeName",
                          "DeceasedEmpName"
                        )}
                      />
                      <InfoRow
                        label={t("emp_contribution")}
                        value={formatCurrency(
                          pickField(
                            item,
                            "DeceasedEmpContributionAmount",
                            "deceasedEmpContributionAmount",
                            "Amount"
                          )
                        )}
                      />
                      <InfoRow
                        label={t("total_contribution")}
                        value={formatCurrency(
                          pickField(
                            item,
                            "TotalAmountContribute",
                            "totalAmountContribute",
                            "TotalContributionAmount"
                          )
                        )}
                      />
                      <button
                        type="button"
                        className="btn btn-danger w-100 mt-2"
                        onClick={() =>
                          navigate(
                            routes.appealContribution.details.replace(
                              ":referenceNumber",
                              encodeURIComponent(refNo)
                            )
                          )
                        }
                      >
                        {t("display_request")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {lastPage > 1 && (
          <div className="d-flex justify-content-center gap-2 mt-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page <= 1 || loading}
              onClick={() => loadList(page - 1)}
            >
              Prev
            </button>
            <span className="align-self-center small text-muted">
              {page} / {lastPage}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page >= lastPage || loading}
              onClick={() => loadList(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default AppealContributionList;
